import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { ChevronDownIcon, LogoutIcon, UserIcon } from './icons';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type Props = {
  name: string;
  email: string;
  avatarUrl?: string;
  profileHref?: string;
  onSignOut?: () => void;
};

export function UserMenu({
  name,
  email,
  avatarUrl,
  profileHref = '/client/profile',
  onSignOut,
}: Props) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full p-1 hover:bg-gray-50"
      >
        <Avatar name={name} url={avatarUrl} />
        <span className="hidden text-left sm:block">
          <span className="block text-sm font-medium text-gray-900">{name}</span>
          <span className="block text-xs text-gray-500">{email}</span>
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </button>
      {open ? (
        <div
          className={cn(
            'absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg',
          )}
        >
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate(profileHref);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
          >
            <UserIcon className="h-4 w-4" /> Profile
          </button>
          <div className="h-px bg-gray-100" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setConfirmOpen(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
          >
            <LogoutIcon className="h-4 w-4" /> Sign out
          </button>
        </div>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Logout"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                onSignOut?.();
              }}
            >
              Log out
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">Are you sure you want to sign out of your account?</p>
      </Dialog>
    </div>
  );
}

function Avatar({ name, url }: { name: string; url?: string }) {
  if (url) return <img src={url} alt="" className="h-9 w-9 rounded-full object-cover" />;
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
      {initials}
    </span>
  );
}
